"use client";

import { Check, Warning } from "./icons/Popup/Icon";
import { Toast, ToastDescription, ToastTitle } from "./toast";
import { useToast } from "../hooks/use-toast";

const SAFE_AREA_TOASTER_PADDING = {
  paddingTop:
    "calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 1rem)",
  paddingBottom:
    "calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 1rem)",
  paddingLeft:
    "calc(var(--safe-area-inset-left, env(safe-area-inset-left, 0px)) + 1rem)",
  paddingRight:
    "calc(var(--safe-area-inset-right, env(safe-area-inset-right, 0px)) + 1rem)",
};

const omitTransientToastFields = <T extends Record<string, unknown>>(
  toastState: T,
) => {
  const toastProps = { ...toastState };

  Reflect.deleteProperty(toastProps, "open");
  Reflect.deleteProperty(toastProps, "onOpenChange");
  Reflect.deleteProperty(toastProps, "duration");

  return toastProps;
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center pointer-events-none sm:justify-end"
      style={SAFE_AREA_TOASTER_PADDING}
    >
      <div className="w-full max-w-[360px] flex flex-col gap-2 sm:items-end">
        {toasts.map((toastItem) => {
          const { id, title, description, action, ...props } =
            omitTransientToastFields(toastItem);
          const variant = props.variant;
          const isSuccess = variant === "success";
          const isDestructive = variant === "destructive";
          const getTitleClassName = () => {
            if (isSuccess) return "text-Color-Text-semantic-positive";
            if (isDestructive) return "text-Color-Text-semantic-negative";
            return "text-Color-Text-neutral-black";
          };
          const titleClassName = getTitleClassName();
          return (
            <Toast key={id} {...props} className="pointer-events-auto">
              {isSuccess && (
                <div className="w-5 h-5 relative overflow-hidden">
                  <Check className="w-5 h-5 text-Color-Icon-positive" />
                </div>
              )}
              {isDestructive && (
                <div className="w-6 h-6 relative overflow-hidden">
                  <Warning className="w-6 h-6 text-Color-Icon-negative" />
                </div>
              )}
              <div className="flex-1 inline-flex flex-col justify-center items-start">
                {title && (
                  <ToastTitle className={titleClassName}>{title}</ToastTitle>
                )}
                {description && (
                  <ToastDescription>{description}</ToastDescription>
                )}
              </div>
              {action}
            </Toast>
          );
        })}
      </div>
    </div>
  );
}
